import { useCallback, useEffect, useRef, useState } from 'react';

import {
  fetchAdminAccounts,
  fetchFormOptions,
} from '../services/createUserService';

const ACCOUNT_SEARCH_DEBOUNCE_MS = 300;

const EMPTY_OPTIONS = {
  jobRoles: [],
  countries: [],
  states: {},
  phoneCountryCodes: [],
  permissions: [],
};

/**
 * Option lists for the Create User wizard (PD-4064).
 *
 * Everything but accounts is bounded, so it loads once when the wizard first
 * opens.
 *
 * Accounts are different: an OEM admin can administer thousands, so they are
 * searched and paged server side. This hook owns that paging — which pages are
 * loaded, how many match in total, whether a request is in flight — and hands the
 * field a flat list plus `loadMoreAccounts`. The field decides when to ask.
 *
 * A search resets to the first page rather than appending, since the results are a
 * different set. `requestId` is what stops a slow page landing on top of a newer
 * search's results.
 *
 * @param {boolean} isOpen fetches on the first open, not on mount, so closing and
 *   reopening the wizard does not refetch.
 */
const useFormOptions = (isOpen) => {
  const [options, setOptions] = useState(EMPTY_OPTIONS);
  const [accountOptions, setAccountOptions] = useState([]);
  const [accountsTotal, setAccountsTotal] = useState(0);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const hasLoaded = useRef(false);

  // The query the loaded pages belong to, so loadMore continues the right search.
  const queryRef = useRef('');

  // Only the newest request may write. Guards two races: a debounced search
  // resolving after a later one, and a loadMore resolving after a search reset.
  const requestId = useRef(0);

  const loadPage = useCallback(async ({ append, query, offset }) => {
    const id = requestId.current + 1;
    requestId.current = id;
    queryRef.current = query;
    setIsLoadingAccounts(true);

    try {
      const { accounts, total } = await fetchAdminAccounts(query, offset);
      if (requestId.current !== id) return;

      // Appended by value rather than concatenated: a page can overlap the one
      // before it when accounts are created between the two requests, and a
      // repeated row would collide on OptionsList's keys.
      setAccountOptions((current) => {
        if (!append) return accounts;

        const seen = new Set(current.map((option) => String(option.value)));
        return [
          ...current,
          ...accounts.filter((option) => !seen.has(String(option.value))),
        ];
      });
      setAccountsTotal(total);
    } catch {
      // A failed page leaves what is already loaded alone rather than emptying the
      // menu under the admin — those accounts are still theirs to pick.
      if (requestId.current === id && !append) {
        setAccountOptions([]);
        setAccountsTotal(0);
      }
    } finally {
      if (requestId.current === id) setIsLoadingAccounts(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen || hasLoaded.current) return undefined;

    hasLoaded.current = true;
    let cancelled = false;

    Promise.all([fetchFormOptions(), fetchAdminAccounts('', 0)])
      .then(([formOptions, firstPage]) => {
        if (cancelled) return;
        setOptions({ ...EMPTY_OPTIONS, ...formOptions });
        setAccountOptions(firstPage.accounts);
        setAccountsTotal(firstPage.total);
      })
      // A failed option list leaves the selects empty rather than breaking the
      // wizard, matching how useFilterOptions treats the same failure. Allow a
      // retry on the next open.
      .catch(() => {
        hasLoaded.current = false;
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const searchTimer = useRef(null);

  useEffect(
    () => () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    },
    [],
  );

  // The field fires this per keystroke, so the debounce lives here, at the request.
  const searchAccounts = useCallback(
    (query) => {
      if (searchTimer.current) clearTimeout(searchTimer.current);

      searchTimer.current = setTimeout(() => {
        loadPage({ append: false, query, offset: 0 });
      }, ACCOUNT_SEARCH_DEBOUNCE_MS);
    },
    [loadPage],
  );

  const hasMoreAccounts = accountOptions.length < accountsTotal;

  /**
   * The next page of the current search.
   *
   * Ignored while a request is in flight, and once everything is loaded, so the
   * scroll handler can call it freely without tracking requests itself.
   */
  const loadMoreAccounts = useCallback(() => {
    if (isLoadingAccounts) return;
    if (accountOptions.length >= accountsTotal) return;

    loadPage({
      append: true,
      query: queryRef.current,
      offset: accountOptions.length,
    });
  }, [accountOptions.length, accountsTotal, isLoadingAccounts, loadPage]);

  return {
    accountOptions,
    accountsTotal,
    hasMoreAccounts,
    isLoadingAccounts,
    loadMoreAccounts,
    options,
    searchAccounts,
  };
};

export default useFormOptions;
